import { useAuth0 } from "@auth0/auth0-react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Typography } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Modal } from "@/atoms";
import { FoldersStructureChildFolderForm, FoldersStructureTabTreeItem } from "@/components";
import { actionsData, constantsData, identifiersData, namesData, permissionsData, statusTypesData } from "@/data";
import { openAlert } from "@/redux/actions";
import { theme } from "@/theme";
import { canDo } from "@/utils";

const FoldersStructureTabTree = ({ actions, selectedTreeItemData, setSelectedTreeItemData }) => {
    const [modalData, setModalData] = useState({
        body: null,
        hasComponent: true,
        id: null,
        open: false,
        title: "",
    });

    const { t } = useTranslation();

    const currentClient = useSelector((state) => state.clientsReducer)?.currentClient;

    const dispatch = useDispatch();

    const foldersStructureTreeData = currentClient?.XMLFolderStructure?.children;

    const { user } = useAuth0();

    const { mediaCreate: mediaCreatePermission, mediaRead: mediaReadPermission } = permissionsData.permissions.keys;

    const { file: fileConstant, folder: folderConstant } = constantsData;

    const treeItemsIds = [];

    const renderTreeItemHandler = (data, parentIndex) =>
        data?.map(({ children, id, name, type }, index) => {
            if (type === folderConstant) {
                return (
                    <FoldersStructureTabTreeItem
                        childrenNumber={children?.filter(({ type: itemType }) => itemType === fileConstant)?.length}
                        itemId={id}
                        key={id}
                        name={name}
                        rowId={parentIndex || parentIndex === 0 ? `${parentIndex + 1}_${index + 1}` : index + 1}
                    >
                        {children?.length > 0 &&
                            renderTreeItemHandler(
                                children,
                                parentIndex || parentIndex === 0 ? `${parentIndex}_${index}` : index,
                            )}
                    </FoldersStructureTabTreeItem>
                );
            }
        });

    const getSelectedTreeItemDataHandler = (id, data) => {
        data?.map(({ children, id: folderId, isSelection, name, parentPath, type }) => {
            if (type === folderConstant) {
                if (id === folderId) {
                    setSelectedTreeItemData({
                        children,
                        id: folderId,
                        isSelection,
                        name,
                        parentPath,
                        type,
                    });
                }

                if (children?.length > 0) {
                    getSelectedTreeItemDataHandler(id, children);
                }
            }
        });
    };

    const getTreeItemsIds = (data) => {
        data?.map(({ children, id, type }) => {
            if (type === folderConstant) {
                treeItemsIds.push(id);

                if (children?.length > 0) getTreeItemsIds(children);
            }
        });

        return treeItemsIds;
    };

    const addFolderHandler = ({ selectableFolderName, textFolderName }) => {
        if (!selectableFolderName && !textFolderName) {
            dispatch(openAlert(t("messages.requiredNewFolderName"), statusTypesData.error));
        } else {
            actions.createFolder({
                isSelection: !!selectableFolderName,
                name: selectableFolderName || textFolderName,
                parentPath:
                    selectedTreeItemData?.parentPath === ""
                        ? selectedTreeItemData?.name
                        : `${selectedTreeItemData?.parentPath}/${selectedTreeItemData?.name}`,
            });

            setModalData({
                ...modalData,
                open: false,
            });
        }
    };

    const clickAddNewFolderButtonHandler = () => {
        setModalData({
            body: (
                <FoldersStructureChildFolderForm
                    action={actionsData.add}
                    setModalData={setModalData}
                    values={{}}
                    onSubmitForm={addFolderHandler}
                />
            ),
            hasComponent: true,
            open: true,
            title: t("actions.addNewFolder"),
        });
    };

    const handleSelectedItemsChange = (event, itemId) => {
        if (itemId) {
            getSelectedTreeItemDataHandler(itemId, foldersStructureTreeData);
        }
    };

    return (
        <Box>
            <Modal modalData={modalData} setModalData={setModalData} />
            <Box
                sx={{
                    mb: 3,
                }}
            >
                <Button
                    color="primary"
                    id={`${namesData.buttons.addNewFolder}_${identifiersData.ids.button}`}
                    variant="outlined"
                    disabled={!selectedTreeItemData?.name || !canDo(user, [mediaCreatePermission, mediaReadPermission])}
                    onClick={clickAddNewFolderButtonHandler}
                >
                    <CreateNewFolderOutlinedIcon sx={{ marginRight: 1 }} />
                    {t("actions.addNewFolder")}
                </Button>
                <Typography
                    color={theme.palette.grey[600]}
                    variant="body1"
                    sx={{
                        mt: 1,
                    }}
                >
                    {t("labels.foldersStructureFolderTitle")}
                </Typography>
            </Box>
            <Box
                sx={{
                    minHeight: 400,
                }}
            >
                <SimpleTreeView
                    defaultExpandedItems={getTreeItemsIds(foldersStructureTreeData)}
                    defaultSelectedItems={selectedTreeItemData?.id}
                    slots={{
                        collapseIcon: ExpandMoreIcon,
                        expandIcon: ChevronRightIcon,
                    }}
                    sx={{
                        flexGrow: 1,
                        height: 264,
                        maxWidth: 400,
                        overflowY: "auto",
                    }}
                    onSelectedItemsChange={handleSelectedItemsChange}
                >
                    {renderTreeItemHandler(foldersStructureTreeData)}
                </SimpleTreeView>
            </Box>
        </Box>
    );
};

export default FoldersStructureTabTree;

FoldersStructureTabTree.propTypes = {
    actions: PropTypes.object,
    selectedTreeItemData: PropTypes.object,
    setSelectedTreeItemData: PropTypes.func,
};
